const DebugOutput = ({ logs }) => {
  console.log('[TaskDebugOutput] Component rendered with logs:', logs ? `${logs.length} chars` : 'null');
  console.log('[TaskDebugOutput] Raw logs:', logs);

  if (!logs) return null;

  const parseLogLine = (line) => {
    const regex =
      /(\d{2}:\d{2}:\d{2}\.\d{2})\s*\(\d+\)\|([^|]+)\|(?:\[(\d+)\]\|)?([^|]*)(?:\|(.*))?/;
    const match = line.match(regex);

    if (!match) {
      console.log('[TaskDebugOutput] parseLogLine - no match for line:', line);
      return null;
    }

    const parsed = {
      timestamp: match[1],
      type: match[2],
      line: match[3],
      level: match[4],
      message: match[5],
    };

    // Log VARIABLE_ASSIGNMENT lines specifically
    if (parsed.type === 'VARIABLE_ASSIGNMENT') {
      console.log('[TaskDebugOutput] Found VARIABLE_ASSIGNMENT:', parsed);
    }

    return parsed;
  };

  const buildTestSections = (logs) => {
    const lines = logs.split("\n");
    let currentTest = null;
    const sections = {};

    for (const line of lines) {
      const markerMatch = line.match(
        /\|DEBUG\|----CAMP_APEX_TEST_NO_(\d+)----/
      );

      if (markerMatch) {
        currentTest = markerMatch[1];
        sections[currentTest] = [];
      } else if (
        currentTest &&
        !line.includes("CODE_UNIT") &&
        !line.includes("EXECUTION_STARTED") &&
        !line.includes("EXECUTION_FINISHED") &&
        line.trim()
      ) {
        sections[currentTest].push(line);
      } else if (
        !currentTest &&
        !line.includes("CODE_UNIT") &&
        !line.includes("EXECUTION_STARTED") &&
        !line.includes("EXECUTION_FINISHED") &&
        line.trim()
      ) {
        // If no test marker found yet, add to default section "0"
        if (!sections["0"]) {
          sections["0"] = [];
        }
        sections["0"].push(line);
      }
    }

    return sections;
  };

  const sections = buildTestSections(logs);
  const testNumbers = Object.keys(sections).sort(
    (a, b) => Number(a) - Number(b)
  );

  console.log('[TaskDebugOutput] Sections:', Object.keys(sections));
  console.log('[TaskDebugOutput] All section lines:', sections);

  // Check if a VARIABLE_ASSIGNMENT line contains an exception
  const extractExceptionFromVariableAssignment = (log) => {
    if (log.type !== "VARIABLE_ASSIGNMENT") return null;

    // For VARIABLE_ASSIGNMENT, the format is:
    // timestamp|VARIABLE_ASSIGNMENT|[line]|variableName|value|memoryAddress
    // So: level = variableName, message = value (which contains the exception message)
    // Check both fields in case the format varies
    const value = log.message || log.level || "";

    console.log('[TaskDebugOutput] extractExceptionFromVariableAssignment checking:', {
      type: log.type,
      level: log.level,
      message: log.message,
      value: value
    });

    // Check for common exception patterns in the value
    const exceptionPatterns = [
      /ExecutionException:\s*(.+)/i,
      /ListException:\s*(.+)/i,
      /NullPointerException/i,
      /DmlException:\s*(.+)/i,
      /QueryException:\s*(.+)/i,
      /TypeException:\s*(.+)/i,
      /MathException:\s*(.+)/i,
      /StringException:\s*(.+)/i,
      /System\.\w+Exception:\s*(.+)/i,
      /out of bounds/i,
      /cannot be null/i,
      /INVALID_/i,
    ];

    for (const pattern of exceptionPatterns) {
      if (pattern.test(value)) {
        console.log('[TaskDebugOutput] Pattern matched:', pattern.toString());
        // Clean up the exception message
        // Remove quotes and the memory address suffix like |0x200a65a4
        let cleanMessage = value
          .replace(/^"/, "")  // Remove leading quote
          .replace(/"$/, "")  // Remove trailing quote
          .replace(/\|0x[a-f0-9]+$/i, "")  // Remove memory address
          .replace(/common\.apex\.runtime\.impl\./g, "")  // Remove internal package prefix
          .trim();

        console.log('[TaskDebugOutput] Cleaned message:', cleanMessage);
        return cleanMessage;
      }
    }

    console.log('[TaskDebugOutput] No pattern matched for VARIABLE_ASSIGNMENT');
    return null;
  };

  const renderLogLine = (log) => {
    // Show DEBUG messages, EXCEPTION logs, FATAL_ERROR logs, and VARIABLE_ASSIGNMENT with exceptions
    const isDebug = log.type.includes("DEBUG");
    const isException = log.type.includes("EXCEPTION") || log.type.includes("FATAL_ERROR");
    const variableAssignmentException = extractExceptionFromVariableAssignment(log);
    const isVariableException = variableAssignmentException !== null;

    if (!isDebug && !isException && !isVariableException) return null;

    // For exceptions, combine level and message fields as both may contain exception details
    let content;
    if (isVariableException) {
      content = variableAssignmentException;
    } else if (isException) {
      content = [log.level, log.message].filter(Boolean).join(' | ');
    } else {
      content = log.message;
    }

    const showAsException = isException || isVariableException;

    return (
      <div
        className="leading-5 px-2.5 py-2 rounded text-[11px]"
        style={{
          background: showAsException
            ? 'rgba(239, 68, 68, 0.1)'
            : 'rgba(39, 39, 42, 0.5)',
          border: showAsException
            ? '1px solid rgba(239, 68, 68, 0.3)'
            : '1px solid rgba(113, 113, 122, 0.2)',
        }}
      >
        {log.line && isDebug && (
          <span className="text-purple-400/90 font-medium mr-2">
            USER_DEBUG
          </span>
        )}
        {isException && (
          <span className="text-red-400 font-medium mr-2">
            {log.type}
          </span>
        )}
        {isVariableException && (
          <span className="text-red-400 font-medium mr-2">
            CAUGHT EXCEPTION
          </span>
        )}
        {content && (
          <span className={showAsException ? "text-red-300" : "text-zinc-300"}>
            {content}
          </span>
        )}
      </div>
    );
  };

  // No logs available
  if (!logs || testNumbers.length === 0) {
    return (
      <div
        className="text-zinc-100 p-4 rounded-lg text-center"
        style={{
          background: 'rgba(24, 24, 27, 0.5)',
          border: '1px solid rgba(113, 113, 122, 0.3)',
        }}
      >
        <p className="text-zinc-400 text-xs">No debug logs available</p>
      </div>
    );
  }

  // Collect all debug logs from all test sections
  const allLogs = testNumbers.flatMap((testNumber) => {
    return (
      sections[testNumber]
        ?.map(parseLogLine)
        .filter((log) => log !== null) || []
    );
  });

  console.log('[TaskDebugOutput] All parsed logs:', allLogs);
  console.log('[TaskDebugOutput] All log types:', allLogs.map(l => l.type));

  // Check for VARIABLE_ASSIGNMENT with exceptions specifically
  const varAssignLogs = allLogs.filter(l => l.type === 'VARIABLE_ASSIGNMENT');
  console.log('[TaskDebugOutput] VARIABLE_ASSIGNMENT logs found:', varAssignLogs.length);
  varAssignLogs.forEach((log, i) => {
    console.log(`[TaskDebugOutput] VARIABLE_ASSIGNMENT ${i}:`, log);
  });

  // Check if there are any EXCEPTION_THROWN or FATAL_ERROR logs
  const hasExplicitExceptionLogs = allLogs.some(log =>
    log.type.includes("EXCEPTION") || log.type.includes("FATAL_ERROR")
  );

  console.log('[TaskDebugOutput] Has explicit exception logs:', hasExplicitExceptionLogs);

  // If we have explicit exception logs, don't bother with VARIABLE_ASSIGNMENT exceptions
  // They would just be duplicates. Only use VARIABLE_ASSIGNMENT for exceptions when
  // there's no EXCEPTION_THROWN (e.g., exception was caught silently)
  let filteredLogs = hasExplicitExceptionLogs
    ? allLogs.filter(log => log.type !== "VARIABLE_ASSIGNMENT")
    : allLogs;

  // Deduplicate EXCEPTION_THROWN and FATAL_ERROR - they always come in pairs for the same exception
  // Just keep EXCEPTION_THROWN (it comes first and has the same info)
  const hasExceptionThrown = filteredLogs.some(log => log.type.includes("EXCEPTION_THROWN"));
  if (hasExceptionThrown) {
    filteredLogs = filteredLogs.filter(log => !log.type.includes("FATAL_ERROR"));
    console.log('[TaskDebugOutput] Filtered out FATAL_ERROR (duplicate of EXCEPTION_THROWN)');
  }

  console.log('[TaskDebugOutput] Filtered logs count:', filteredLogs.length);

  return (
    <div
      className="text-zinc-100 rounded-lg font-mono text-xs max-h-[400px] overflow-y-auto"
      style={{
        background: 'rgba(24, 24, 27, 0.5)',
      }}
    >
      <div className="space-y-1.5 p-3">
        {filteredLogs.map((log, logIndex) => (
          <div key={logIndex}>{renderLogLine(log)}</div>
        ))}
      </div>
    </div>
  );
};

export default DebugOutput;
